import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Clock, TrendingUp, DollarSign, FileText, CreditCard, Users, ArrowLeft, ExternalLink, Info } from 'lucide-react';
import { useAPI } from 'components/lib';
import styles from './healthScore.module.css';
import metricsConfigRaw from './healthScoreConfig.json';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { createRoot } from 'react-dom/client';

// Convert icon string to actual icon component after import
const iconMap = {
  'AlertCircle': AlertCircle,
  'CheckCircle': CheckCircle,
  'Clock': Clock,
  'TrendingUp': TrendingUp,
  'DollarSign': DollarSign,
  'FileText': FileText,
  'CreditCard': CreditCard,
  'Users': Users
};

// Convert imported JSON config to add iconComponent
const metricsConfig = Object.fromEntries(
  Object.entries(metricsConfigRaw).map(([key, value]) => [
    key,
    {
      ...value,
      iconComponent: iconMap[value.icon] || FileText
    }
  ])
);

export function HealthScore({ config = metricsConfig }) {
  const apiResult = useAPI('/api/appData/detailedHealthScore');
  const [overallScore, setOverallScore] = useState(0);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [healthData, setHealthData] = useState({});

  useEffect(() => {
    setHealthData(apiResult.data);
    setOverallScore(
      apiResult.data?.overall_score || 0
    );
  }, [apiResult.data]);

  // Move pdfRefs above the loading check to ensure hooks are not called conditionally
  const pdfRefs = React.useMemo(() => ({
    cover: React.createRef(),
    summary: React.createRef(),
    details: Object.entries((healthData && healthData.scores) || {}).map(() => React.createRef())
  }), [healthData]);

  if (apiResult.loading) {
    return <div style={{textAlign: 'center', padding: '40px'}}>Loading health score...</div>;
  }

  if (typeof apiResult.data === 'undefined') {
    return <div style={{textAlign: 'center', padding: '40px', color: '#64748b', fontWeight: 500, fontSize: 18}}>
      The health score for this connection is not yet available.<br />
      Please check back later after processing is complete.
    </div>;
  }

  if (!healthData || !healthData.scores) {
    return <div style={{textAlign: 'center', padding: '40px'}}>Loading health score...</div>;
  }

  // Get metric configuration with fallback
  const getMetricConfig = (key) => {
    const configItem = config[key];
    if (!configItem) {
      return {
        name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        icon: 'FileText',
        explanation: 'Score is calculated based on data quality and completeness for this metric.',
        howItWorks: ['Score calculated based on various data quality factors'],
        improvementTips: ['Review data regularly for accuracy and completeness']
      };
    }
    
    return {
      ...configItem,
      iconComponent: iconMap[configItem.icon] || FileText
    };
  };


  const getColor = (score) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    return 'red';
  };

  const handleMetricClick = (metricKey, metricData) => {
    const configData = getMetricConfig(metricKey);
    setSelectedMetric({
      key: metricKey,
      name: configData.name,
      icon: configData.iconComponent,
      explanation: configData.explanation,
      howItWorks: configData.howItWorks,
      improvementTips: configData.improvementTips,
      ...metricData
    });
  };

  const handleBackClick = () => {
    setSelectedMetric(null);
  };


  // Modern, styled PDF export using html2canvas and jsPDF
  const metricsArray = Object.entries((healthData && healthData.scores) || {})
    .map(([key, data]) => {
      const configData = getMetricConfig(key);
      return {
        key,
        name: configData.name,
        score: data.score,
        reason: data.reason,
        icon: configData.iconComponent,
        issues: data.issues || [],
        total_issues: data.total_issues
      };
    })
    .sort((a, b) => a.score - b.score);
  const priorityActions = metricsArray.slice(0, 3);

  const renderPdfSections = () => (
    <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '420pt', background: '#f8fafc', zIndex: -1, fontFamily: 'Inter, Arial, sans-serif' }}>
      {/* Cover Page */}
      <div ref={pdfRefs.cover} style={{height: '595pt', width: '420pt', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f0f4f8 0%, #e0e7ef 100%)'}}>
        <h1 style={{fontSize: 36, marginBottom: 18, color: '#1e293b', fontWeight: 700, letterSpacing: '-2px'}}>Accounting Health Report</h1>
        <h2 style={{fontSize: 22, marginBottom: 12, color: '#64748b', fontWeight: 500}}>LedgerView</h2>
        <div style={{fontSize: 14, color: '#64748b', marginBottom: 24}}>Generated: {new Date().toLocaleString()}</div>
        <div style={{fontSize: 13, color: '#334155', background: '#e0e7ef', borderRadius: 12, padding: '10px 18px', boxShadow: '0 2px 8px #cbd5e1'}}>Modern Health Insights for Your Accounting Data</div>
      </div>
      {/* PDF Summary Page - Redesigned */}
      <div ref={pdfRefs.summary} style={{height: '595pt', width: '420pt', padding: 24, boxSizing: 'border-box', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px #e0e7ef', display: 'flex', flexDirection: 'column', gap: 16}}>
        {/* Title and Date */}
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6}}>
          <h1 style={{fontSize: 20, color: '#1e293b', fontWeight: 700, margin: 0}}>Accounting Health Summary</h1>
          <span style={{fontSize: 11, color: '#64748b'}}>Generated: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
        {/* Overall Score and Monitor Success Rate - Improved GaugeChart */}
        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 18, marginBottom: 6}}>
          <div style={{flex: '0 0 120pt', background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px #e0e7ef', padding: 12, textAlign: 'center', minWidth: 90, maxWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
            {/* Improved GaugeChart for PDF */}
            <div style={{background: '#fff', borderRadius: '50%', padding: 6, marginBottom: 4}}>
              <svg width={70} height={70}>
                <circle cx="35" cy="35" r="28" stroke="#e5e7eb" strokeWidth="7" fill="none" />
                <circle
                  cx="35" cy="35" r="28"
                  stroke={overallScore >= 80 ? '#10b981' : overallScore >= 60 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="7" fill="none"
                  strokeDasharray={2 * Math.PI * 28}
                  strokeDashoffset={2 * Math.PI * 28 - (overallScore / 100) * 2 * Math.PI * 28}
                  strokeLinecap="round"
                />
                <text x="35" y="41" textAnchor="middle" fontSize="18" fontWeight="bold" fill={overallScore >= 80 ? '#10b981' : overallScore >= 60 ? '#f59e0b' : '#ef4444'}>{Math.round(overallScore)}</text>
                <text x="35" y="54" textAnchor="middle" fontSize="8" fill="#64748b">Score</text>
              </svg>
            </div>
            <div style={{fontSize: 13, fontWeight: 700, color: overallScore >= 80 ? '#10b981' : overallScore >= 60 ? '#f59e0b' : '#ef4444', margin: '4px 0 2px'}}>
              {overallScore >= 80 ? 'Excellent' : overallScore >= 60 ? 'Fair' : 'Poor'}
            </div>
            <div style={{fontSize: 10, color: '#64748b'}}>Overall Score</div>
          </div>
          <div style={{flex: 1, background: '#f8fafc', borderRadius: 10, boxShadow: '0 1px 4px #e0e7ef', padding: 12, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
            <div style={{fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4}}>Monitor Success Rate</div>
            <div style={{fontSize: 10, color: '#64748b', marginBottom: 4}}>Percentage of records that passed all monitoring rules.</div>
            <div style={{fontSize: 18, fontWeight: 700, color: '#0ea5e9'}}>
              {typeof healthData.monitor_success_rate === 'number' ? Math.round(healthData.monitor_success_rate * 100) : '--'}%
            </div>
          </div>
        </div>
        {/* Priority Actions */}
        <div style={{margin: '6px 0'}}>
          <h3 style={{fontSize: 13, color: '#334155', fontWeight: 700, margin: 0, marginBottom: 4}}>Priority Actions</h3>
          <div style={{display: 'flex', flexDirection: 'column', gap: 5}}>
            {priorityActions.map((action) => {
              const color = getColor(action.score);
              return (
                <div key={action.key} style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#1e293b', background: '#f8fafc', borderRadius: 6, padding: '5px 8px', boxShadow: '0 1px 2px #e0e7ef'}}>
                  <div style={{width: 8, height: 8, borderRadius: '50%', background: color === 'red' ? '#ef4444' : color === 'yellow' ? '#f59e0b' : '#10b981'}} />
                  <span style={{fontWeight: 600}}>{action.name}:</span> <span style={{color: '#64748b'}}>{action.reason}</span>
                </div>
              );
            })}
          </div>
        </div>
        {/* Individual Metrics List - 2 Columns */}
        <div style={{marginTop: 6}}>
          <h3 style={{fontSize: 13, color: '#334155', fontWeight: 700, margin: 0, marginBottom: 4}}>Metric Details</h3>
          <div style={{display: 'flex', flexDirection: 'row', gap: 10}}>
            {/* Split metrics into two columns */}
            <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: 6}}>
              {metricsArray.filter((_, i) => i % 2 === 0).map((metric) => {
                const Icon = metric.icon;
                const color = getColor(metric.score);
                return (
                  <div key={metric.key} style={{display: 'flex', alignItems: 'flex-start', background: '#f8fafc', borderRadius: 6, boxShadow: '0 1px 2px #e0e7ef', padding: 7, gap: 7}}>
                    <div style={{background: color === 'green' ? '#d1fae5' : color === 'yellow' ? '#fef9c3' : '#fee2e2', borderRadius: '50%', width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1}}>
                      <Icon style={{color: color === 'green' ? '#10b981' : color === 'yellow' ? '#f59e0b' : '#ef4444', width: 10, height: 10}} />
                    </div>
                    <div style={{flex: 1}}>
                      <div style={{fontSize: 10, fontWeight: 600, color: '#1e293b'}}>{metric.name}</div>
                      <div style={{fontSize: 9, color: '#64748b', marginBottom: 1}}>{metric.reason}</div>
                      <div style={{fontSize: 9, color: '#64748b', marginBottom: 1}}>Score: <span style={{fontWeight: 700, color: color === 'green' ? '#10b981' : color === 'yellow' ? '#f59e0b' : '#ef4444'}}>{Math.round(metric.score)}%</span></div>
                      {metric.issues && metric.issues.length > 0 && (
                        <div style={{fontSize: 8, color: '#ef4444', marginTop: 1}}>
                          Issues: {metric.issues.length}{metric.total_issues && metric.total_issues > metric.issues.length ? ` (+${metric.total_issues - metric.issues.length} more)` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: 6}}>
              {metricsArray.filter((_, i) => i % 2 === 1).map((metric) => {
                const Icon = metric.icon;
                const color = getColor(metric.score);
                return (
                  <div key={metric.key} style={{display: 'flex', alignItems: 'flex-start', background: '#f8fafc', borderRadius: 6, boxShadow: '0 1px 2px #e0e7ef', padding: 7, gap: 7}}>
                    <div style={{background: color === 'green' ? '#d1fae5' : color === 'yellow' ? '#fef9c3' : '#fee2e2', borderRadius: '50%', width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1}}>
                      <Icon style={{color: color === 'green' ? '#10b981' : color === 'yellow' ? '#f59e0b' : '#ef4444', width: 10, height: 10}} />
                    </div>
                    <div style={{flex: 1}}>
                      <div style={{fontSize: 10, fontWeight: 600, color: '#1e293b'}}>{metric.name}</div>
                      <div style={{fontSize: 9, color: '#64748b', marginBottom: 1}}>{metric.reason}</div>
                      <div style={{fontSize: 9, color: '#64748b', marginBottom: 1}}>Score: <span style={{fontWeight: 700, color: color === 'green' ? '#10b981' : color === 'yellow' ? '#f59e0b' : '#ef4444'}}>{Math.round(metric.score)}%</span></div>
                      {metric.issues && metric.issues.length > 0 && (
                        <div style={{fontSize: 8, color: '#ef4444', marginTop: 1}}>
                          Issues: {metric.issues.length}{metric.total_issues && metric.total_issues > metric.issues.length ? ` (+${metric.total_issues - metric.issues.length} more)` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {/* Metric Details Pages */}
      {metricsArray.map((metric, idx) => {
        const configData = getMetricConfig(metric.key);
        const color = getColor(metric.score);
        const Icon = metric.icon;
        return (
          <div ref={pdfRefs.details[idx]} key={metric.key} style={{height: '792pt', width: '612pt', padding: 32, boxSizing: 'border-box', background: '#fff', borderRadius: 18, boxShadow: '0 2px 12px #e0e7ef', fontFamily: 'Inter, Arial, sans-serif'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 18, marginBottom: 24}}>
              <div style={{background: color === 'green' ? '#d1fae5' : color === 'yellow' ? '#fef9c3' : '#fee2e2', borderRadius: '50%', width: 54, height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Icon style={{color: color === 'green' ? '#10b981' : color === 'yellow' ? '#f59e0b' : '#ef4444', width: 32, height: 32}} />
              </div>
              <div>
                <h1 style={{fontSize: 30, color: '#1e293b', fontWeight: 700, margin: 0}}>{metric.name}</h1>
                <div style={{fontSize: 16, color: '#64748b', marginTop: 2}}>Issues requiring attention</div>
              </div>
            </div>
            <div style={{display: 'flex', gap: 32, marginBottom: 24}}>
              <div style={{flex: 1}}>
                <div style={{fontSize: 16, color: '#64748b'}}>Current Score</div>
                <div style={{fontSize: 32, fontWeight: 700, color: color === 'green' ? '#10b981' : color === 'yellow' ? '#f59e0b' : '#ef4444', margin: '8px 0'}}>{Math.round(metric.score)}%</div>
              </div>
              <div style={{flex: 2}}>
                <div style={{fontSize: 16, color: '#64748b'}}>Reason</div>
                <div style={{fontSize: 16, color: '#334155', margin: '8px 0'}}>{metric.reason}</div>
              </div>
            </div>
            {/* Issues list */}
            {metric.issues && metric.issues.length > 0 && (
              <div style={{marginBottom: 24}}>
                <h3 style={{fontSize: 18, color: '#ef4444', fontWeight: 600, marginBottom: 8}}>Issues to Resolve ({metric.total_issues || metric.issues.length})</h3>
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                  {metric.issues.map((issue, index) => (
                    <div key={index} style={{display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', borderRadius: 8, padding: '8px 14px', fontSize: 15, color: '#1e293b'}}>
                      <div style={{width: 10, height: 10, borderRadius: '50%', background: color === 'red' ? '#ef4444' : color === 'yellow' ? '#f59e0b' : '#10b981'}} />
                      <span style={{fontWeight: 500}}>{issue.name}</span>
                      {issue.url ? (
                        <span style={{color: '#2563eb', marginLeft: 8}}>Has Link</span>
                      ) : (
                        <span style={{color: '#64748b', marginLeft: 8}}>No direct link</span>
                      )}
                    </div>
                  ))}
                  {metric.total_issues && metric.total_issues > metric.issues.length && (
                    <div style={{ textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
                      and {metric.total_issues - metric.issues.length} more like this...
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* How This Metric Works */}
            <div style={{marginBottom: 18, background: '#f1f5f9', borderRadius: 10, padding: 18}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6}}>
                <Info style={{color: '#0ea5e9', width: 20, height: 20}} />
                <span style={{fontWeight: 600, color: '#1e293b'}}>How This Score Is Calculated</span>
              </div>
              <div style={{fontSize: 15, color: '#334155', marginBottom: 6}}>{configData.explanation}</div>
              <ul style={{margin: 0, paddingLeft: 18, color: '#64748b', fontSize: 15}}>
                {configData.howItWorks?.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            {/* Improvement Tips */}
            <div style={{marginBottom: 18, background: '#f1f5f9', borderRadius: 10, padding: 18}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6}}>
                <CheckCircle style={{color: '#10b981', width: 20, height: 20}} />
                <span style={{fontWeight: 600, color: '#1e293b'}}>Tips to Improve This Score</span>
              </div>
              <ul style={{margin: 0, paddingLeft: 18, color: '#64748b', fontSize: 15}}>
                {configData.improvementTips?.map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>
            {/* Action summary */}
            <div style={{background: '#f8fafc', borderRadius: 10, padding: 18}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6}}>
                <AlertCircle style={{color: '#ef4444', width: 20, height: 20}} />
                <span style={{fontWeight: 600, color: '#1e293b'}}>Next Steps</span>
              </div>
              <div style={{fontSize: 15, color: '#334155'}}>
                Click "Open Record" to navigate directly to each item in your accounting system and make the necessary corrections. Once resolved, refresh this dashboard to see your updated score.
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const handleDownloadPDF = async () => {
    // Render hidden sections
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);
    root.render(renderPdfSections());
    await new Promise((res) => setTimeout(res, 600));
    // Use A5 size (420x595 pt)
    const doc = new jsPDF({ unit: 'pt', format: [420, 595] });
    const pageWidth = 420;
    const pageHeight = 595;
    // Helper to add a page from a ref
    const addPageFromRef = async (ref, isFirst = false) => {
      const el = ref.current;
      // Use PNG for best quality, scale=2 for sharper images
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: null });
      const imgData = canvas.toDataURL('image/png'); // PNG format
      if (!isFirst) doc.addPage([pageWidth, pageHeight]);
      doc.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);
    };
    // Cover
    await addPageFromRef(pdfRefs.cover, true);
    // Summary
    await addPageFromRef(pdfRefs.summary);
    // Details
    for (let i = 0; i < pdfRefs.details.length; i++) {
      await addPageFromRef(pdfRefs.details[i]);
    }
    doc.save('Accounting-Health-Report.pdf');
    root.unmount();
    document.body.removeChild(container);
  };

  const GaugeChart = ({ score, size = 120 }) => {
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (score / 100) * circumference;
    const color = getColor(score);
    
    return (
      <div className={styles.gaugeChartWrapper}>
        <svg width={size} height={size} className={styles.gaugeChartSvg}>
          <circle cx="60" cy="60" r="45" stroke="#e5e7eb" strokeWidth="8" fill="none" />
          <circle
            cx="60" cy="60" r="45"
            stroke={color === 'green' ? '#10b981' : color === 'yellow' ? '#f59e0b' : '#ef4444'}
            strokeWidth="8" fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className={styles.gaugeChartCircle}
          />
        </svg>
        <div className={styles.gaugeChartCenter}>
          <span className={
            color === 'green' ? styles.scoreGreen : 
            color === 'yellow' ? styles.scoreYellow : styles.scoreRed
          }>
            {Math.round(overallScore)}
          </span>
          <span className={styles.scoreLabel}>Score</span>
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Detail view for selected metric
  if (selectedMetric) {
    const color = getColor(selectedMetric.score);
    return (
      <div className={styles.dashboardContainer}>
        {/* Header with back button */}
        <div className={styles.headerRow}>
          <button 
            onClick={handleBackClick}
            className={styles.backButton}
          >
            <ArrowLeft className={styles.backIcon} />
            Back to Dashboard
          </button>
          <div className={styles.headerMetricRow}>
            <div className={
              color === 'green' ? styles.metricIconGreen :
              color === 'yellow' ? styles.metricIconYellow :
              styles.metricIconRed
            }>
              <selectedMetric.icon className={styles.metricIconSvg} />
            </div>
            <div>
              <h1 className={styles.metricTitle}>{selectedMetric.name}</h1>
              <p className={styles.metricSubtitle}>Issues requiring attention</p>
            </div>
          </div>
        </div>

        {/* Metric summary */}
        <div className={styles.metricSummaryBox}>
          <div className={styles.metricSummaryRow}>
            <div>
              <div className={styles.metricSummaryLabel}>Current Score</div>
              <div className={
                color === 'green' ? styles.metricScoreGreen :
                color === 'yellow' ? styles.metricScoreYellow :
                styles.metricScoreRed
              }>
                {Math.round(selectedMetric.score)}%
              </div>
            </div>
            <div className={styles.metricSummaryRight}>
              <div className={styles.metricSummaryLabel}>Reason</div>
              <div className={styles.metricSummaryWork}>{selectedMetric.reason}</div>
            </div>
          </div>
        </div>

        {/* Issues list */}
        {selectedMetric.issues && selectedMetric.issues.length > 0 && (
          <div className={styles.issuesBox}>
            <div className={styles.issuesHeader}>
              <h3 className={styles.issuesTitle}>
                Issues to Resolve ({selectedMetric.total_issues || selectedMetric.issues.length})
              </h3>
            </div>
            <div className={styles.issuesList}>
              {selectedMetric.issues.map((issue, index) => (
                <div key={index} className={styles.issuesListItem}>
                  <div className={styles.issuesListRow}>
                    <div className={styles.issuesListLeft}>
                      <div className={
                        color === 'red' ? styles.issueDotRed :
                        color === 'yellow' ? styles.issueDotYellow :
                        styles.issueDotGreen
                      } />
                      <span className={styles.issuesListName}>{issue.name}</span>
                    </div>
                    {issue.url ? (
                      <a
                        href={issue.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.issuesListLink}
                      >
                        Open Record
                        <ExternalLink className={styles.issuesListLinkIcon} />
                      </a>
                    ) : (
                      <span className={styles.issuesListNoLink}></span>
                    )}
                  </div>
                </div>
              ))}
              {selectedMetric.total_issues && selectedMetric.total_issues > selectedMetric.issues.length && (
                <div className={styles.issuesListItem} style={{ textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
                  and {selectedMetric.total_issues - selectedMetric.issues.length} more like this...
                </div>
              )}
            </div>
          </div>
        )}

        {/* How This Metric Works */}
        <div className={styles.metricHowBox}>
          <div className={styles.metricHowRow}>
            <Info className={styles.metricHowIcon} />
            <div className={styles.metricHowContent}>
              <h4 className={styles.metricHowTitle}>How This Score Is Calculated</h4>
              <p className={styles.metricHowExplanation}>{selectedMetric.explanation}</p>
              <div className={styles.metricHowList}>
                {selectedMetric.howItWorks?.map((item, index) => (
                  <div key={index} className={styles.metricHowListItem}>
                    <div className={styles.metricHowBullet} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Improvement Tips */}
        <div className={styles.tipsBox}>
          <div className={styles.tipsRow}>
            <CheckCircle className={styles.tipsIcon} />
            <div className={styles.tipsContent}>
              <h4 className={styles.tipsTitle}>Tips to Improve This Score</h4>
              <div className={styles.tipsList}>
                {selectedMetric.improvementTips?.map((tip, index) => (
                  <div key={index} className={styles.tipsListItem}>
                    <div className={styles.tipsBullet} />
                    <span>{tip}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action summary */}
        <div className={styles.nextStepsBox}>
          <div className={styles.nextStepsRow}>
            <AlertCircle className={styles.nextStepsIcon} />
            <div>
              <h4 className={styles.nextStepsTitle}>Next Steps</h4>
              <p className={styles.nextStepsText}>
                Click "Open Record" to navigate directly to each item in your accounting system and make the necessary corrections. 
                Once resolved, refresh this dashboard to see your updated score.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main dashboard view
  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.dashboardHeader}>
        <h1 className={styles.dashboardTitle}>Accounting Health</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span className={styles.dashboardUpdated}>
            Updated: {formatDate(healthData.timestamp)}
          </span>
          {/* <button className={styles.refreshButton} onClick={handleRefresh}>
            <RefreshCw className={styles.refreshIcon} />
            Refresh
          </button> */}
          <button className={styles.refreshButton} onClick={handleDownloadPDF}>
            Download as PDF
          </button>
        </div>
      </div>
      
      {/* Main Score & Grid */}
      <div className={styles.dashboardGrid}>
        {/* Overall Score */}
        <div className={styles.overallScoreBox}>
          <div className={styles.overallScoreCenter}>
            <GaugeChart score={overallScore} />
            <div className={
              overallScore >= 80 ? styles.overallScoreExcellent :
              overallScore >= 60 ? styles.overallScoreFair :
              styles.overallScorePoor
            }>
              {overallScore >= 80 ? 'Excellent' : overallScore >= 60 ? 'Fair' : 'Poor'}
            </div>
            <div className={styles.scoreRangesModern}>
              <div className={styles.scoreRangeModernRed}></div>
              <div className={styles.scoreRangeModernYellow}></div>
              <div className={styles.scoreRangeModernGreen}></div>
            </div>
            <div className={styles.scoreRangesLabels}>
              <span style={{ width: '70px' }} className={styles.scoreRangeLabel}>0-59 Poor</span>
              <span style={{ width: '60px' }} className={styles.scoreRangeLabel}>60-79 Fair</span>
              <span style={{ width: '80px' }} className={styles.scoreRangeLabel}>80-100 Excellent</span>
            </div>
          </div>
        </div>

        {/* Metrics Grid with Monitor Success Rate as first row */}
        <div className={styles.metricsGrid}>
          {typeof healthData.monitor_success_rate === 'number' && (
            <div className={styles.metricCard} style={{ gridColumn: '1 / -1', marginBottom: 18, background: '#f8fafc', display: 'flex', alignItems: 'center', padding: 24, borderRadius: 14, boxShadow: '0 1px 4px #e0e7ef' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#334155', flex: 1 }}>Monitor Success Rate</div>
              <div style={{ fontSize: 18, color: '#64748b', flex: 2 }}>
                This is the percentage of records that passed all monitoring rules.
              </div>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#0ea5e9', flex: 0, minWidth: 90, textAlign: 'right' }}>
                {Math.round(healthData.monitor_success_rate * 100)}%
              </div>
            </div>
          )}
          {metricsArray.map((metric) => {
            const Icon = metric.icon;
            const color = getColor(metric.score);
            return (
              <div 
                key={metric.key} 
                className={styles.metricCard}
                onClick={() => handleMetricClick(metric.key, healthData.scores[metric.key])}
              >
                <div className={styles.metricCardHeader}>
                  <div className={
                    color === 'green' ? styles.metricCardIconGreen :
                    color === 'yellow' ? styles.metricCardIconYellow :
                    styles.metricCardIconRed
                  }>
                    <Icon className={styles.metricCardIconSvg} />
                  </div>
                  <span className={
                    color === 'green' ? styles.metricCardScoreGreen :
                    color === 'yellow' ? styles.metricCardScoreYellow :
                    styles.metricCardScoreRed
                  }>
                    {Math.round(metric.score)}%
                  </span>
                </div>
                <div className={styles.metricCardBody}>
                  <div className={styles.metricCardName}>{metric.name}</div>
                  <div className={styles.metricCardWork}>{metric.reason}</div>
                  <div className={styles.metricCardBarBg}>
                    <div 
                      className={
                        color === 'green' ? styles.metricCardBarGreen :
                        color === 'yellow' ? styles.metricCardBarYellow :
                        styles.metricCardBarRed
                      }
                      style={{ width: `${metric.score}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Priority Actions */}
      <div className={styles.priorityBox}>
        <h3 className={styles.priorityTitle}>Priority Actions</h3>
        <div className={styles.priorityList}>
          {priorityActions.map((action) => {
            const color = getColor(action.score);
            return (
              <div 
                key={action.key} 
                className={styles.priorityListItem}
                onClick={() => handleMetricClick(action.key, healthData.scores[action.key])}
              >
                <div className={
                  color === 'red' ? styles.priorityDotRed :
                  color === 'yellow' ? styles.priorityDotYellow :
                  styles.priorityDotGreen
                } />
                <span className={styles.priorityListText}>
                  {action.name}: {action.reason}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}